import React, { FC, useCallback, useLayoutEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { displayLibxGenres } from '../../../store/header/actions';
import ContentBaseLayout from '../shared/ContentBaseLayout';
import { updateProductInProductsList } from '../../../store/libx/actions';
import NoElementsFound, { NO_ELEMENTS_PRODUCTS } from '../../../shared/components/NoElementsFound';
import Pagination, { getPaginationStateObject } from '../../../shared/common/Pagination';
import ProductList from '../../../shared/common/ProductList';
import LibxchangeProductElement from '../shared/LibxchangeProductElement';
import { LibxGenresSidebar } from './LibxGenresSidebar';
import { useLocalContentGenres } from './api/useLocalContentGenres';
import { useQueryParams } from '../../../shared/hooks/useQueryParams';
import queryString from 'query-string';
import { ProductSorting } from '../../../../models/Product';
import { ageCategoryFromString } from '../../../../models/AgeCategory';
import { deserializeFilter } from '../../../shared/utils/deserializeFilter';
import { DEFAULT_PAGE_SIZE } from '../../collection/by-genre/CollectionByGenrePage';
import { useHistory, useLocation, useParams } from 'react-router';
import { useLibxProducts } from './api/useLibxProducts';
import { modifyURLState, modifyURLStates } from '../../../shared/utils/utils-routing';
import { DISPLAY_TYPE_MINIMAL, DISPLAY_TYPE_TILE } from '../../../store/displayType/types';
import { createBreadcrumbItems } from '../../../store/breadcrumbs/actions';
import { BreadcrumbsState } from '../../../store/breadcrumbs/state';
import BreadCrumbNav from '../../../shared/navigation/BreadcrumbNav';
import { FilterBar } from '../../../shared/common/FilterBar';
import { ResponsiveFilterBar } from '../../../shared/filter/ResponsiveFilterBar';
import { fetchLibxGenreProductsAggregationsQuickSearch } from '../../../api/marketplace/fetchLibxGenreProductsAggregationsQuickSearch';
import { useAddLibxProductToCollection } from '../productdetail/api/useAddLibxProductToCollection';
import { AppState } from '../../../store/appState';
import { QueryKeyBuilder } from '../../../shared/filter/useAggregationsQuery';
import { FetchAggregations } from '../../../api/listContent/fetchAggregations';
import { fetchLibxGenreAggregations } from '../../../api/marketplace/fetchLibxGenreAggregations';
import { FetchAggregationsQuickSearch } from '../../../api/listContent/fetchAggregationsQuickSearch';
import { defaultVisibleFilterGroups } from '../../../shared/filter/defaultVisibleFilterGroups';

const StResponsiveFilterBar = styled(ResponsiveFilterBar)`
    margin-bottom: 100px;
`;

const StFilterBar = styled(FilterBar)`
    padding: 0 0 25px 0;
`;

const StHeader = styled.div`
    height: 64px;
`;

const SORT_BY_QUERY_PARAM = 'sortBy';
const PAGE_QUERY_PARAM = 'page';
type GenreParams = { genre?: string; subGenre?: string };

const getSelectedGenre = ({ genre, subGenre }: GenreParams) =>
    genre ? decodeURIComponent(genre) + (subGenre ? `->${decodeURIComponent(subGenre)}` : '') : undefined;

export const LibxGenresContainer: FC = () => {
    const dispatch = useDispatch();
    const authentication = useSelector((state: AppState) => state.authentication);

    const { location, replace } = useHistory();
    const { search } = useLocation();

    const { data: genres, isLoading: isGenresLoading, defaultGenre } = useLocalContentGenres();
    const { mutate: addToCollectionMutation, isLoading: isAddToCollectionLoading } = useAddLibxProductToCollection();

    const { genre, subGenre } = useParams<GenreParams>();

    const { displayFilter: displayFilterQueryParam } = queryString.parse(search);
    const displayFilter = displayFilterQueryParam === null;

    const standardGenres = genres?.standardGenres ?? [];
    const localContentGenres = genres?.localContentGenres ?? [];

    useLayoutEffect(() => {
        dispatch(displayLibxGenres());
    }, [dispatch]);

    const {
        page: { value: pageParam = 1 },
        pageSize: { value: pageSizeParam },
        sortBy: { value: sortByParam },
        filter: { value: filterQueryParam },
        ageCategory: { value: ageCategoryQueryParam },
        display: { value: display }
    } = useQueryParams({
        [PAGE_QUERY_PARAM]: 'integer',
        [SORT_BY_QUERY_PARAM]: 'string',
        pageSize: 'integer',
        filter: 'string',
        ageCategory: 'string',
        display: 'string'
    });

    const selectedGenre = getSelectedGenre({ genre, subGenre }) ?? defaultGenre;
    const page = pageParam - 1 ?? 0;
    const sortBy = sortByParam ?? ProductSorting.RELEASE_DATE_NEW_TO_OLD;
    const pageSize = pageSizeParam ?? DEFAULT_PAGE_SIZE;
    const ageCategory = ageCategoryFromString(ageCategoryQueryParam);
    const filter = deserializeFilter(filterQueryParam);

    //region FILTERBAR_V2
    const visibleFilterGroups = useMemo(
        () => defaultVisibleFilterGroups(authentication, ageCategory),
        [ageCategory, authentication]
    );
    const aggregationsQueryKeyBuilder = useCallback<QueryKeyBuilder>(
        ({ filters, expandedFilterGroups }) => [
            'libXGenrePage',
            selectedGenre,
            'aggregation',
            ageCategory,
            filters,
            expandedFilterGroups
        ],
        [ageCategory, selectedGenre]
    );
    const fetchAggregations = useCallback<FetchAggregations>(
        options => fetchLibxGenreAggregations({ ...options, ageCategory, genre: selectedGenre! }),
        [ageCategory, selectedGenre]
    );
    const fetchAggregationsQuickSearch = useCallback<FetchAggregationsQuickSearch>(
        options => fetchLibxGenreProductsAggregationsQuickSearch({ ...options, genre: selectedGenre!, ageCategory }),
        [ageCategory, selectedGenre]
    );
    //endregion

    const { isLoading: isProductsLoading, data: libxProducts } = useLibxProducts({
        ageCategory,
        genre: selectedGenre,
        filterMap: filter,
        pagination: { sortBy, page, pageSize },
        enabled: !isGenresLoading
    });
    const totalProducts = libxProducts?.totalHits ?? 0;
    const products = libxProducts?.hits ?? [];

    const buckets = createBreadcrumbItems([...standardGenres, ...localContentGenres], 'subGenres');
    const breadcrumbs: BreadcrumbsState = {
        key: 'by-genre',
        label: 'By Genre',
        buckets,
        activeBucket: selectedGenre
    };

    const breadcrumbNavLinkParams = `?${queryString.stringify({
        ageCategory,
        display,
        displayFilter: displayFilterQueryParam
    })}`;

    const setSortBy = nextSortBy => {
        const pageQueryState = getPaginationStateObject();
        const sortByQueryState = { name: SORT_BY_QUERY_PARAM, value: nextSortBy, encode: false };

        const nextSearch = modifyURLStates(location.search, [pageQueryState, sortByQueryState]);

        replace({ pathname: location.pathname, search: nextSearch });
    };

    function onDisplayChange(nextDisplayType) {
        const nextSearch = modifyURLState(location.search, 'display', nextDisplayType, false);
        replace({ pathname: location.pathname, search: nextSearch });
    }

    const setPage = nextPage => {
        const nextSearch = modifyURLState(
            location.search,
            PAGE_QUERY_PARAM,
            nextPage !== 0 ? nextPage + 1 : undefined,
            false
        );

        replace({ pathname: location.pathname, search: nextSearch });
    };

    function onFilterDisplayToggle() {
        const nextSearch = modifyURLState(location.search, 'displayFilter', !displayFilter, false);
        replace({ pathname: location.pathname, search: nextSearch });
    }

    const onUpdateProductInProductsList = id => {
        dispatch(updateProductInProductsList(id));
    };

    const loading = isProductsLoading || isGenresLoading || isAddToCollectionLoading;

    return (
        <ContentBaseLayout
            documentTitle="Genres"
            topComponent={
                <>
                    <StHeader>
                        <BreadCrumbNav
                            breadcrumbs={breadcrumbs}
                            fixedLabel="Genres"
                            path="libxchange"
                            customGenreBase="genres"
                            floatingClass=""
                            queryParamString={breadcrumbNavLinkParams}
                        />
                    </StHeader>
                    <StFilterBar
                        dispatch={dispatch}
                        loading={loading}
                        onSortChange={setSortBy}
                        totalProducts={totalProducts}
                        hideSelectButtons={true}
                        currentPage={page}
                        pageSize={pageSize}
                        onPageChange={setPage}
                        onFilterDisplayToggle={onFilterDisplayToggle}
                        hideGroupBy={true}
                        displayType={display ?? DISPLAY_TYPE_MINIMAL}
                        sortBy={sortBy}
                        onDisplayChange={onDisplayChange}
                        excludeDisplayTypes={[DISPLAY_TYPE_TILE]}
                        excludeSorting={[
                            'narratorAZ',
                            'stock',
                            'reserve',
                            'durationLongToShort',
                            'durationShortToLong',
                            'priceHighToLow',
                            'priceLowToHigh'
                        ]}
                        displayFilter={displayFilter}
                    />
                </>
            }
            sidebarComponent={
                selectedGenre ? (
                    displayFilter ? (
                        <StResponsiveFilterBar
                            visibleFilterGroups={visibleFilterGroups}
                            buildQueryKey={aggregationsQueryKeyBuilder}
                            fetchAggregations={fetchAggregations}
                            fetchAggregationsQuickSearch={fetchAggregationsQuickSearch}
                        />
                    ) : (
                        <LibxGenresSidebar
                            localContentGenres={genres?.localContentGenres ?? []}
                            standardGenres={genres?.standardGenres ?? []}
                            selectedGenre={selectedGenre}
                        />
                    )
                ) : null
            }
            contentComponent={
                (totalProducts === 0 && !loading) || (!selectedGenre && !loading) ? (
                    <NoElementsFound type={NO_ELEMENTS_PRODUCTS} />
                ) : (
                    <div className="ca-main-inner">
                        <ProductList
                            products={products}
                            totalProducts={totalProducts}
                            listItemComponent={LibxchangeProductElement}
                            tileItemComponent={LibxchangeProductElement}
                            onAddToCollection={productId => addToCollectionMutation(productId)}
                            updateProduct={onUpdateProductInProductsList}
                            displayType={display}
                            currentPage={page}
                        />
                        <Pagination
                            totalItems={totalProducts}
                            onPageChange={setPage}
                            currentPage={page}
                            pageSize={pageSize}
                        />
                    </div>
                )
            }
            loading={loading}
        />
    );
};
